

import type { WorldState, TriggeredEvent, BattleReward, HeroState, RewardEffect, EventPayload } from "@/core";
import { applyRewardEffect } from "@/core";
import { ACTIVE_DECK_SIZE, CLASS_DEFS, DOMAIN_DEFS, EQUIPMENT_CATALOG, REPUTATION_UPGRADES, TEMPLE_BUFF_DURATION_TURNS, grantXp, nextReputationCost, recomputeHeroDerived, type CardDef, type ClassId, type DomainId, type EquipmentSlot, type ChoiceKind, type ReputationUpgradeId, type Stat } from "@/core/roscha";
import { SimpleEmitter } from "../app/emitter";
import type { FloatingRewardEvent } from "../app/GameApp";
import type { GameTransport, MoveResult, RestoreTempleResult } from "./GameTransport";
import { authFetch } from "../auth/authFetch";

type Events = {
  worldState: WorldState;
  triggeredEvent: TriggeredEvent;
  energyUpdate: number;
  rewardApplied: RewardEffect;
  floatingReward: FloatingRewardEvent;
};

export interface ServerTransportConfig {
  apiBaseUrl: string; // e.g. "http://localhost:8000"
}

interface RoomResponse {
  id: string;
  userId: string;
  seed: number;
  stateJson: WorldState;
  energy: number;
  turn: number;
}

interface FogChange {
  x: number;
  y: number;
}

interface MoveDelta {
  hero: { x: number; y: number };
  fogRevealed: FogChange[];
  collectedResources: string[];
  triggeredEvents: Array<{ eventId?: string; payload?: unknown }>;
  energy: number;
  energyCost: number;
  turn: number;
}

interface MoveResponse {
  ok: boolean;
  reason?: string;
  energyCost?: number;
  delta?: MoveDelta;
  energy?: number;
}

interface BuildResponse {
  ok: boolean;
  reason?: string;
  stateJson?: WorldState;
}

export class ServerTransport implements GameTransport {
  private emitter = new SimpleEmitter<Events>();
  private roomId: string | null = null;
  private energy = 0;
  private currentState: WorldState | null = null;
  
  private heroPersistChain: Promise<void> = Promise.resolve();

  constructor(private cfg: ServerTransportConfig) {}

  start() {
    void this.bootstrap();
  }

  stop() {
  }

  getEnergy(): number {
    return this.energy;
  }

  onWorldState(cb: (state: WorldState) => void) {
    return this.emitter.on("worldState", cb);
  }

  onTriggeredEvent(cb: (event: TriggeredEvent) => void) {
    return this.emitter.on("triggeredEvent", cb);
  }

  onEnergyUpdate(cb: (energy: number) => void) {
    return this.emitter.on("energyUpdate", cb);
  }

  onRewardApplied(cb: (e: RewardEffect) => void) {
    return this.emitter.on("rewardApplied", cb);
  }

  onFloatingReward(cb: (f: FloatingRewardEvent) => void) {
    return this.emitter.on("floatingReward", cb);
  }

  async move(x: number, y: number): Promise<MoveResult> {
    if (!this.roomId) return { ok: false, reason: "Room not loaded" };

    try {
      const res = await this.fetchJson<MoveResponse>(
        `/rooms/${this.roomId}/move`,
        { method: "POST", body: JSON.stringify({ x, y }) },
      );

      if (res.ok && res.delta && this.currentState) {
        const collectedSnapshots: Array<{ tileX: number; tileY: number; type: "gold" | "mana"; amount: number }> = [];
        for (const resId of res.delta.collectedResources) {
          const inst = this.currentState.resources.byId[resId];
          if (!inst) continue;
          const tiles = this.currentState.map.tiles;
          let found: { x: number; y: number } | null = null;
          for (let y = 0; y < tiles.length && !found; y++) {
            for (let x = 0; x < tiles[y].length; x++) {
              const t = tiles[y][x];
              if (t.content.kind === "resource" && t.content.resourceId === resId) {
                found = { x: t.x, y: t.y };
                break;
              }
            }
          }
          if (found) {
            collectedSnapshots.push({ tileX: found.x, tileY: found.y, type: inst.resourceType, amount: inst.amount });
          }
        }

        const goldBefore = this.currentState.hero.inventory.resources.gold ?? 0;
        const manaBefore = this.currentState.hero.inventory.resources.mana ?? 0;

        this.applyDelta(res.delta);

        const triggeredRewardEffects: RewardEffect[] = [];
        const syntheticLevelUps: number[] = [];
        for (const evt of res.delta.triggeredEvents) {
          const p = (evt.payload ?? {}) as { kind?: string; effect?: RewardEffect | null; effects?: RewardEffect[] };
          if (p.kind === "reward") {
            const list = p.effects && p.effects.length > 0 ? p.effects : (p.effect ? [p.effect] : []);
            for (const eff of list) {
              applyRewardEffect(this.currentState, eff);
              triggeredRewardEffects.push(eff);
              if (eff.type === "xp") {
                const curLevel = this.currentState.hero.level ?? 1;
                const r = grantXp(this.currentState.hero.xp ?? 0, curLevel, 0);
                if (r.leveledUp) {
                  this.currentState.hero.xp = r.newXp;
                  for (let l = curLevel + 1; l <= curLevel + r.levelsGained; l++) syntheticLevelUps.push(l);
                }
              }
            }
          }
        }
        for (const newLevel of syntheticLevelUps) {
          res.delta.triggeredEvents.push({
            eventId: `auto-level-up-${newLevel}`,
            payload: { kind: "level_up", newLevel } as unknown as Record<string, unknown>,
          });
        }

        this.emitter.emit("worldState", this.currentState);

        for (const snap of collectedSnapshots) {
          this.emitter.emit("floatingReward", {
            icon: snap.type === "gold" ? "🪙" : "💧",
            iconTexture: snap.type === "gold" ? "resIconGold" : "resIconMana",
            label: `+${snap.amount}`,
            color: snap.type === "gold" ? "#fbbf24" : "#8b5cf6",
            tileX: snap.tileX,
            tileY: snap.tileY,
          });
        }

        for (const eff of triggeredRewardEffects) {
          if (eff.type !== "resource") this.emitter.emit("rewardApplied", eff);
        }

        const collectedGold = collectedSnapshots.filter((s) => s.type === "gold").reduce((sum, s) => sum + s.amount, 0);
        const collectedMana = collectedSnapshots.filter((s) => s.type === "mana").reduce((sum, s) => sum + s.amount, 0);
        const goldAfter = this.currentState.hero.inventory.resources.gold ?? 0;
        const manaAfter = this.currentState.hero.inventory.resources.mana ?? 0;
        const goldFromRewards = goldAfter - goldBefore - collectedGold;
        const manaFromRewards = manaAfter - manaBefore - collectedMana;
        if (goldFromRewards > 0) this.emitter.emit("rewardApplied", { type: "resource", resourceType: "gold", amount: goldFromRewards });
        if (manaFromRewards > 0) this.emitter.emit("rewardApplied", { type: "resource", resourceType: "mana", amount: manaFromRewards });
        if (res.energy != null) {
          this.energy = res.energy;
          this.emitter.emit("energyUpdate", this.energy);
        }

        for (const evt of res.delta.triggeredEvents) {
          this.emitter.emit("triggeredEvent", { eventId: evt.eventId ?? "", payload: evt.payload as EventPayload });
        }
      }

      return {
        ok: res.ok,
        reason: res.reason,
        energyCost: res.energyCost,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "move failed";
      return { ok: false, reason: msg };
    }
  }

  private applyDelta(delta: MoveDelta) {
    if (!this.currentState) return;

    this.currentState.hero.x = delta.hero.x;
    this.currentState.hero.y = delta.hero.y;

    for (const tile of delta.fogRevealed) {
      this.currentState.map.tiles[tile.y][tile.x].fog.discovered = true;
    }

    for (const resId of delta.collectedResources) {
      if (!this.currentState.resources.collectedIds.includes(resId)) {
        this.currentState.resources.collectedIds.push(resId);
      }
      const res = this.currentState.resources.byId[resId];
      if (res) {
        if (!this.currentState.hero.inventory.resources) {
          this.currentState.hero.inventory.resources = {};
        }
        const cur = this.currentState.hero.inventory.resources[res.resourceType] ?? 0;
        this.currentState.hero.inventory.resources[res.resourceType] = cur + res.amount;
      }
      for (const row of this.currentState.map.tiles) {
        for (const t of row) {
          if (t.content.kind === "resource" && t.content.resourceId === resId) {
            t.content = { kind: "empty" };
          }
        }
      }
    }

    for (const evt of delta.triggeredEvents) {
      if (!evt.eventId) continue;
      if (!this.currentState.events.triggeredIds.includes(evt.eventId)) {
        this.currentState.events.triggeredIds.push(evt.eventId);
      }
      const entry = this.currentState.events.byId[evt.eventId];
      if (entry) entry.triggered = true;
    }

    this.currentState.turn = delta.turn;
    this.expireBuffs(this.currentState);

    const heroTile = this.currentState.map.tiles[delta.hero.y]?.[delta.hero.x];
    if (heroTile) heroTile.fog.visited = true;
  }

  private inFlight = new Set<string>();

  async restoreTemple(kind: "hp" | "energy"): Promise<RestoreTempleResult> {
    if (!this.roomId) return { ok: false, reason: "No room" };
    const key = `restoreTemple:${kind}`;
    if (this.inFlight.has(key)) return { ok: false, reason: "Запрос уже выполняется" };
    this.inFlight.add(key);
    try {
      const res = await this.fetchJson<{ ok: boolean; reason?: string; cost?: number; stateJson?: WorldState; energy?: number }>(
        `/rooms/${this.roomId}/temple-restore`,
        { method: "POST", body: JSON.stringify({ kind }) },
      );
      if (res.ok && res.stateJson) {
        this.currentState = res.stateJson;
        this.emitter.emit("worldState", res.stateJson);
      }
      if (res.ok && res.energy != null) {
        this.energy = res.energy;
        this.emitter.emit("energyUpdate", res.energy);
      }
      return { ok: res.ok, reason: res.reason, cost: res.cost };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Запрос не удался";
      return { ok: false, reason: msg };
    } finally {
      this.inFlight.delete(key);
    }
  }

  async buildInTown(townId: string, buildingId: string): Promise<boolean> {
    if (!this.roomId) return false;

    try {
      const res = await this.fetchJson<BuildResponse>(
        `/rooms/${this.roomId}/build`,
        { method: "POST", body: JSON.stringify({ townId, buildingId }) },
      );

      if (res.ok && res.stateJson) {
        this.currentState = res.stateJson;
        this.emitter.emit("worldState", res.stateJson);
      }

      return res.ok;
    } catch {
      return false;
    }
  }


  
  resolveBattle(reward: BattleReward): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    hero.hp = Math.max(0, Math.min(hero.maxHp, reward.heroHpAfter));

    if (reward.won) {
      hero.reputation += reward.reputationGained;
      hero.inventory.resources.gold = (hero.inventory.resources.gold ?? 0) + reward.goldGained;
      if (reward.equipmentAdded) {
        const items = hero.inventory.items;
        const existing = items.find((i) => i.itemId === reward.equipmentAdded);
        if (existing) existing.qty += 1;
        else items.push({ itemId: reward.equipmentAdded, qty: 1 });
      }
    }
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  equipItem(itemId: string): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const def = EQUIPMENT_CATALOG[itemId];
    if (!def) return;
    hero.equipment[def.slot] = itemId;
    recomputeHeroDerived(hero);
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  unequipSlot(slot: EquipmentSlot): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    hero.equipment[slot] = null;
    recomputeHeroDerived(hero);
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  buyTavernCard(cardId: string, goldCost: number): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const gold = hero.inventory.resources.gold ?? 0;
    if (gold < goldCost) return;
    hero.inventory.resources.gold = gold - goldCost;
    hero.collection.push(cardId);
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  applyReputationUpgrade(upgradeId: ReputationUpgradeId): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const def = REPUTATION_UPGRADES[upgradeId];
    if (!def) return;
    const currentLevel = hero.reputationUpgrades[upgradeId] ?? 0;
    const cost = nextReputationCost(def, currentLevel);
    if (cost === null) return;
    if (hero.reputation < cost) return;
    hero.reputation -= cost;
    hero.reputationUpgrades[upgradeId] = currentLevel + 1;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  resetFear(goldCost: number): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const gold = hero.inventory.resources.gold ?? 0;
    if (gold < goldCost) return;
    hero.inventory.resources.gold = gold - goldCost;
    hero.fearAccumulated = 0;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  applyTempleBuff(stat: Stat, goldCost: number): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const gold = hero.inventory.resources.gold ?? 0;
    if (gold < goldCost) return;
    hero.inventory.resources.gold = gold - goldCost;
    hero.baseStats[stat] = (hero.baseStats[stat] ?? 0) + 1;
    if (!hero.activeBuffs) hero.activeBuffs = [];
    hero.activeBuffs.push({ stat, amount: 1, expiresAtTurn: this.currentState.turn + TEMPLE_BUFF_DURATION_TURNS });
    recomputeHeroDerived(hero);
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  private expireBuffs(state: WorldState): void {
    const hero = state.hero;
    if (!hero.activeBuffs || hero.activeBuffs.length === 0) return;
    const remaining: typeof hero.activeBuffs = [];
    let changed = false;
    for (const buff of hero.activeBuffs) {
      if (buff.expiresAtTurn <= state.turn) {
        hero.baseStats[buff.stat] = Math.max(0, (hero.baseStats[buff.stat] ?? 0) - buff.amount);
        changed = true;
      } else {
        remaining.push(buff);
      }
    }
    hero.activeBuffs = remaining;
    if (changed) recomputeHeroDerived(hero);
  }

  applyCharacterCreation(classId: ClassId, domainId: DomainId): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const classDef = CLASS_DEFS[classId];
    const domainDef = DOMAIN_DEFS[domainId];
    hero.classId = classId;
    hero.domainId = domainId;
    hero.baseStats = { ...classDef.startingStats };
    hero.baseStats[domainDef.keyStat] = 8;
    const startingCards = [...classDef.startingClassCards];
    while (startingCards.length < ACTIVE_DECK_SIZE) startingCards.push("basic_defend");
    hero.collection = [...startingCards];
    hero.deck = startingCards.slice(0, ACTIVE_DECK_SIZE);
    hero.equipment = { head: null, body: null, hands: null, weapon: null, accessory: null };
    recomputeHeroDerived(hero);
    hero.hp = hero.maxHp;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  private upgradeOwnedCard(hero: WorldState["hero"], cardId: string): boolean {
    if (cardId.endsWith("_plus")) return false;
    const ci = hero.collection.indexOf(cardId);
    if (ci < 0) return false;
    hero.collection[ci] = `${cardId}_plus`;
    const di = hero.deck.indexOf(cardId);
    if (di >= 0) hero.deck[di] = `${cardId}_plus`;
    return true;
  }

  upgradeDeckCard(cardId: string, goldCost: number): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const gold = hero.inventory.resources.gold ?? 0;
    if (gold < goldCost) return;
    if (!this.upgradeOwnedCard(hero, cardId)) return;
    hero.inventory.resources.gold = gold - goldCost;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  setActiveDeck(deck: string[]): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    const available = [...hero.collection];
    const valid: string[] = [];
    for (const id of deck) {
      const idx = available.indexOf(id);
      if (idx < 0) continue;
      available.splice(idx, 1);
      valid.push(id);
      if (valid.length >= ACTIVE_DECK_SIZE) break;
    }
    hero.deck = valid;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  applyLevelUpChoice(choice: ChoiceKind): void {
    if (!this.currentState) return;
    const hero = this.currentState.hero;
    switch (choice.kind) {
      case "addCard":
        hero.collection.push(choice.cardId);
        break;
      case "statPlusOne":
        hero.baseStats[choice.stat] = (hero.baseStats[choice.stat] ?? 0) + 1;
        recomputeHeroDerived(hero);
        if (choice.stat === "constitution") hero.hp += 5;
        break;
    }
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  advanceHeroLevel(): void {
    if (!this.currentState) return;
    this.currentState.hero.level += 1;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
  }

  applyXp(amount: number): number {
    if (!this.currentState) return 0;
    const hero = this.currentState.hero;
    const r = grantXp(hero.xp ?? 0, hero.level, amount);
    hero.xp = r.newXp;
    hero.level = r.newLevel;
    this.emitter.emit("worldState", this.currentState);
    this.schedulePersist();
    return r.levelsGained;
  }


  persistHero(hero: HeroState): Promise<void> {
    if (!this.roomId) return Promise.resolve();
    const roomId = this.roomId;
    const snapshot = JSON.parse(JSON.stringify(hero)) as HeroState;
    this.heroPersistChain = this.heroPersistChain
      .catch(() => undefined)  
      .then(() =>
        this.fetchJson<{ ok: boolean; turn: number }>(`/rooms/${roomId}/hero`, {
          method: "PUT",
          body: JSON.stringify({ hero: snapshot }),
        }).then(() => undefined)
          .catch((e) => {
            console.warn("[ServerTransport] persistHero failed:", e);
          }),
      );
    return this.heroPersistChain;
  }

  
  private schedulePersist(): void {
    if (!this.currentState) return;
    void this.persistHero(this.currentState.hero);
  }

  resetWorld(): Promise<void> {
    return this.fetchJson<{ ok: boolean }>(`/rooms/me`, { method: "DELETE" }).then(() => undefined);
  }

  fetchCardCatalog(): Promise<CardDef[]> {
    return this.fetchJson<Array<{
      id: string; name: string; classId: string | null; enhancements: unknown;
      kind: string; rarity: string; hope: unknown; fear: unknown;
    }>>(`/cards`).then((rows) =>
      rows.map((r) => ({
        id: r.id,
        name: r.name,
        classId: (r.classId ?? undefined) as CardDef["classId"],
        enhancements: (r.enhancements ?? {}) as CardDef["enhancements"],
        kind: r.kind as CardDef["kind"],
        rarity: r.rarity as CardDef["rarity"],
        hope: r.hope as CardDef["hope"],
        fear: r.fear as CardDef["fear"],
      })),
    );
  }

  private async bootstrap() {
    try {
      const room = await this.fetchJson<RoomResponse>("/rooms/me");
      this.roomId = room.id;
      this.energy = room.energy;
      this.currentState = room.stateJson;
      this.expireBuffs(this.currentState);
      this.emitter.emit("worldState", room.stateJson);
      this.emitter.emit("energyUpdate", this.energy);
    } catch (e) {
      console.error("[ServerTransport] bootstrap failed:", e);
    }
  }

  async refreshState(): Promise<void> {
    try {
      const room = await this.fetchJson<RoomResponse>("/rooms/me");
      this.energy = room.energy;
      this.currentState = room.stateJson;
      this.emitter.emit("worldState", room.stateJson);
      this.emitter.emit("energyUpdate", this.energy);
    } catch (e) {
      console.error("[ServerTransport] refreshState failed:", e);
    }
  }

  private async fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${this.cfg.apiBaseUrl}${path}`;
    const res = await authFetch(url, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...init?.headers,
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`${res.status} ${text}`);
    }

    return (await res.json()) as T;
  }
}
